import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { History } from 'lucide-react';

function diffLines(a, b) {
  const A = (a || '').split('\n'), B = (b || '').split('\n');
  const m = A.length, n = B.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (A[i] === B[j]) { out.push({ type: 'same', text: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: 'del', text: A[i] }); i++; }
    else { out.push({ type: 'add', text: B[j] }); j++; }
  }
  while (i < m) out.push({ type: 'del', text: A[i++] });
  while (j < n) out.push({ type: 'add', text: B[j++] });
  return out;
}

const fmtAt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

const ROW_CLS = {
  same: 'text-gray-700',
  add: 'bg-emerald-100 text-emerald-900',
  del: 'bg-red-100 text-red-800 line-through decoration-red-400/60',
};

export function LetterHistoryDialog({ open, onOpenChange, letter }) {
  const versions = letter?.body_versions || [];
  const [selV, setSelV] = useState(null);
  useEffect(() => { if (open) setSelV(null); }, [open]);

  const latest = versions.length ? versions[versions.length - 1].v : 1;
  const current = selV ?? latest;
  const idx = versions.findIndex(v => v.v === current);
  const ver = versions[idx];
  const prev = idx > 0 ? versions[idx - 1] : null;

  const rows = useMemo(() => {
    if (!ver) return [];
    if (!prev) return (ver.body || '').split('\n').map(t => ({ type: 'same', text: t }));
    return diffLines(prev.body, ver.body);
  }, [ver, prev]);

  const changed = rows.filter(r => r.type !== 'same').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl" data-testid="letter-history-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <History className="w-5 h-5 text-sky-400" /> Letter history — {letter?.company_name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5" data-testid="letter-version-chips">
          {versions.map(v => (
            <button key={v.v} type="button" onClick={() => setSelV(v.v)} data-testid={`letter-version-${v.v}`}
              title={`${v.by || '—'} · ${fmtAt(v.at)}`}
              className={`text-[11px] border rounded-sm px-2.5 py-1.5 transition-colors font-mono ${current === v.v
                ? 'border-sky-500/60 bg-sky-600/15 text-sky-200'
                : 'border-border bg-secondary/40 text-muted-foreground hover:text-foreground'}`}>
              v{v.v}{v.v === latest ? ' · current' : ''}
            </button>
          ))}
        </div>
        {ver && (
          <p className="text-[11px] text-muted-foreground" data-testid="letter-version-meta">
            {prev
              ? <>Changes from <span className="font-mono text-foreground">v{prev.v}</span> to <span className="font-mono text-foreground">v{ver.v}</span> — {changed} line{changed === 1 ? '' : 's'} changed · by {ver.by || '—'} · {fmtAt(ver.at)}</>
              : <>Original version · by {ver.by || '—'} · {fmtAt(ver.at)}</>}
          </p>
        )}
        <div className="bg-white border border-border rounded-sm p-4 max-h-[52vh] overflow-y-auto" data-testid="letter-diff-panel">
          {rows.map((r, i) => (
            <div key={i} className={`text-xs font-mono leading-relaxed whitespace-pre-wrap px-1 ${ROW_CLS[r.type]}`}>
              {r.text || '\u00A0'}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
